// @/services/scm-permissions/storage.js

/**
 * Storage utility for SCM permissions
 * 
 * Provides a consistent interface for storing and retrieving permissions
 * from sessionStorage with timestamp validation and error handling.
 * 
 * Features:
 * - Automatic timestamp tracking for freshness validation
 * - Error recovery with graceful degradation
 * - Consistent data structure enforcement
 * - Partial updates with selective property modification
 * - Cross-session persistence with automatic cleanup
 */
export const permissionsStorage = {
  /**
   * Storage key for SCM permissions
   * @type {string}
   */
  STORAGE_KEY: 'scmPermissions',
  
  /**
   * Save permissions to sessionStorage
   * Persists permission data with timestamp and validation
   * 
   * @param {Object} permissions Permissions object
   * @returns {boolean} Success status
   */
  savePermissions(permissions) {
    if (!permissions) return false;
    
    try {
      // Make sure we have sessionStorage available
      if (typeof sessionStorage === 'undefined') {
        return false;
      }
      
      // Add timestamp for cache validation
      const permissionsToSave = {
        ...permissions,
        lastUpdated: permissions.lastUpdated || new Date().toISOString()
      };
      
      // Stringify and save
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(permissionsToSave));
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to save SCM permissions to storage:', error);
      }
      return false;
    }
  },
  
  /**
   * Get permissions from sessionStorage
   * Retrieves and parses permission data with error handling
   * 
   * @returns {Object|null} Permissions object or null if not found
   */
  getPermissions() {
    try {
      // Make sure we have sessionStorage available
      if (typeof sessionStorage === 'undefined') {
        return null;
      }
      
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;
      
      return JSON.parse(stored);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to get SCM permissions from storage:', error);
      }
      return null;
    }
  },
  
  /**
   * Clear permissions from sessionStorage
   * Removes all permission data from persistence
   * 
   * @returns {boolean} Success status
   */
  clearPermissions() {
    try {
      // Make sure we have sessionStorage available
      if (typeof sessionStorage === 'undefined') {
        return false;
      }
      
      sessionStorage.removeItem(this.STORAGE_KEY);
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to clear SCM permissions from storage:', error);
      }
      return false;
    }
  },
  
  /**
   * Update specific permission properties
   * Selectively modifies individual properties while preserving others
   * 
   * @param {Object} updates Permission updates to apply
   * @returns {boolean} Success status
   */
  updatePermissions(updates) {
    if (!updates) return false;
    
    try {
      const current = this.getPermissions();
      if (!current) return false;
      
      const updated = {
        ...current,
        ...updates,
        lastUpdated: new Date().toISOString()
      };
      
      return this.savePermissions(updated);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to update SCM permissions in storage:', error);
      }
      return false;
    }
  }
};